import { useState, useEffect, useMemo } from "react";
import { Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/shared/components/ui/Modal";
import { Input } from "@/shared/components/ui/Input";
import { Button } from "@/shared/components/ui/Button";
import { fetchCompanies, updateFolder } from "@/features/files/services/sharepointService";
import type { SharepointFolder } from "@/features/files/types/sharepoint";

interface FolderSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  folder: SharepointFolder;
  onUpdated: (updated: SharepointFolder) => void;
}

export const FolderSettingsModal = ({
  isOpen,
  onClose,
  folder,
  onUpdated,
}: FolderSettingsModalProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Seed form from the folder each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setName(folder.name);
      setSelectedIds([...folder.company_ids]);
      setSearch("");
      setError("");
      fetchCompanies().then(setCompanies).catch(() => {});
    }
  }, [isOpen, folder]);

  const filteredCompanies = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return companies;
    return companies.filter((c) => c.name.toLowerCase().includes(q));
  }, [companies, search]);

  const selectedCompanies = useMemo(
    () => companies.filter((c) => selectedIds.includes(c.id)),
    [companies, selectedIds],
  );

  const removeCompany = (id: string) =>
    setSelectedIds((prev) => prev.filter((cid) => cid !== id));

  const addCompany = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t("sharepoint.folder_name_required"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload: { name: string; company_ids?: string[] } = { name: trimmed };
      if (!folder.parent_id) payload.company_ids = selectedIds;
      const updated = await updateFolder(folder.id, payload);
      onUpdated(updated);
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data
          ?.error ||
        (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data
          ?.message ||
        t("sharepoint.update_failed");
      console.error("Update folder error:", err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={folder.parent_id ? t("sharepoint.rename_folder") : t("sharepoint.folder_settings")}>
      <div className="space-y-4">
        {/* Folder name */}
        <Input
          label={t("sharepoint.folder_name")}
          placeholder={t("sharepoint.enter_folder_name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        {/* Company assignment — root folders only */}
        {!folder.parent_id && (
          <>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  {t("sharepoint.assigned_companies")}
                </label>
                <div className="flex gap-2">
                  {selectedIds.length < companies.length && (
                    <button
                      type="button"
                      onClick={() => setSelectedIds(companies.map((c) => c.id))}
                      className="text-xs text-indigo-600 hover:text-indigo-800"
                    >
                      {t("sharepoint.select_all")}
                    </button>
                  )}
                  {selectedIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedIds([])}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      {t("sharepoint.remove_all")}
                    </button>
                  )}
                </div>
              </div>
              {selectedCompanies.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {selectedCompanies.map((c) => (
                    <span
                      key={c.id}
                      className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs bg-indigo-100 text-indigo-700 border border-indigo-200"
                    >
                      {c.name}
                      <button
                        type="button"
                        onClick={() => removeCompany(c.id)}
                        className="p-0.5 rounded-full hover:bg-indigo-200"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-amber-600">
                  {t("sharepoint.no_companies_hint")}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t("sharepoint.search_companies")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {filteredCompanies.length === 0 ? (
                  <p className="px-3 py-4 text-sm text-gray-400 text-center">
                    {t("sharepoint.no_companies_found")}
                  </p>
                ) : (
                  filteredCompanies.map((c) => {
                    const isSelected = selectedIds.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => (isSelected ? removeCompany(c.id) : addCompany(c.id))}
                        className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left transition-colors ${
                          isSelected
                            ? "bg-indigo-50 text-indigo-700"
                            : "hover:bg-gray-50 text-gray-700"
                        }`}
                      >
                        {c.name}
                        {isSelected && (
                          <span className="text-xs text-indigo-500">{t("sharepoint.selected")}</span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} className="flex-1">
            {t("sharepoint.cancel")}
          </Button>
          <Button onClick={handleSave} loading={loading} className="flex-1">
            {t("sharepoint.save")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
